import { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  BulkPromotionService,
  BulkPromotionServiceOptionsResponse,
  BulkPromotionServiceOptionsVariables,
  GET_BULK_PROMOTION_SERVICE_OPTIONS,
} from '@/features/Profile/sections/MyAds/api';
import { useCurrentUser } from '@/features/Profile/core/hooks';
import { useCreatePayment } from '@/hooks/useCreatePayment';
import { usePlatformSource } from '@/hooks/usePlatformSource';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import {
  CreatePaymentResponse,
  PaymentMethod,
  PaymentResult,
  PaymentStatus,
} from '@/api/types/payments.types';
import { MutationError } from '@/api/types/common.types';

type Props = {
  targetIds: string[];
  onPaymentSuccess: (payment: PaymentResult) => void;
};

export function useMyAdsBulkPromotionPayment({
  targetIds,
  onPaymentSuccess,
}: Props) {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const source = usePlatformSource();
  const [isServicePickerOpen, toggleServicePicker] = useToggleOpenState();
  const [selectedServiceType, setSelectedServiceType] = useState<string | null>(
    null
  );
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState('');
  const [
    isNotEnoughFundsOpen,
    toggleNotEnoughFunds,
    openNotEnoughFunds,
    closeNotEnoughFunds,
  ] = useToggleOpenState();
  const [isAddBalanceOpen, toggleAddBalance, openAddBalance] =
    useToggleOpenState();
  const [isBalanceTopUpSuccessOpen, toggleBalanceTopUpSuccess] =
    useToggleOpenState();
  const [
    loadServiceOptions,
    { data, error: serviceOptionsError, loading: isServiceOptionsLoading },
  ] = useLazyQuery<
    BulkPromotionServiceOptionsResponse,
    BulkPromotionServiceOptionsVariables
  >(GET_BULK_PROMOTION_SERVICE_OPTIONS, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });
  const { createPayment, loading: isPaymentLoading } = useCreatePayment({
    onSuccess: handlePaymentSuccess,
    onError: handlePaymentError,
  });

  const services = data?.paymentServiceOptions ?? [];
  const selectedService =
    services.find(service => service.serviceType === selectedServiceType) ??
    null;
  const selectedOption =
    selectedService?.options.find(
      option => option.serviceId === selectedOptionId
    ) ?? null;
  const visiblePaymentError =
    paymentError ||
    (isServicePickerOpen ? serviceOptionsError?.message ?? '' : '');

  function openServicePicker() {
    setPaymentError('');
    toggleServicePicker(true);
    loadServiceOptions({ variables: { targetIds } });
  }

  function closeServicePicker() {
    toggleServicePicker(false);
  }

  function selectService(service: BulkPromotionService) {
    const defaultOption =
      service.options.find(option => option.default) ?? service.options[0];

    if (!defaultOption) return;

    setSelectedServiceType(service.serviceType);
    setSelectedOptionId(defaultOption.serviceId);
    closeServicePicker();
  }

  function closePayment() {
    setSelectedServiceType(null);
    setSelectedOptionId(null);
  }

  function selectOption(serviceId: string) {
    setSelectedOptionId(serviceId);
  }

  function handlePaymentSuccess(response: CreatePaymentResponse) {
    onPaymentSuccess(response.createPayment.entity);
  }

  function handlePaymentError(status: PaymentStatus, errors?: MutationError[]) {
    if (errors?.some(error => error.code === 'insufficient_funds')) {
      openNotEnoughFunds();
      return;
    }

    const message =
      status === PaymentStatus.Failure ? errors?.[0]?.message : undefined;
    setPaymentError(
      message ?? t('pages.cabinet.bulk_promotion_payment_failed')
    );
  }

  async function pay() {
    if (!selectedOption || isPaymentLoading) return;

    if ((user.walletBalance ?? 0) < selectedOption.amount) {
      openNotEnoughFunds();
      return;
    }

    try {
      await createPayment({
        variables: {
          serviceId: selectedOption.serviceId,
          targetIds,
          paymentMethod: PaymentMethod.Wallet,
          source,
        },
      });
    } catch {
      handlePaymentError(PaymentStatus.Failure);
    }
  }

  function startAddBalance() {
    closeNotEnoughFunds();
    openAddBalance();
  }

  function closePaymentError() {
    setPaymentError('');

    if (serviceOptionsError) {
      closeServicePicker();
    }
  }

  return {
    closePayment,
    closePaymentError,
    closeServicePicker,
    isAddBalanceOpen,
    isBalanceTopUpSuccessOpen,
    isNotEnoughFundsOpen,
    isPaymentLoading,
    isServiceOptionsLoading,
    isServicePickerOpen,
    openServicePicker,
    pay,
    paymentError: visiblePaymentError,
    selectedOption,
    selectedOptionId,
    selectedService,
    selectOption,
    selectService,
    services,
    startAddBalance,
    toggleAddBalance,
    toggleBalanceTopUpSuccess,
    toggleNotEnoughFunds,
    user,
  };
}
